import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import styles from './SuperLessons.module.scss';
import IconRotating from 'src/assets/icons/rotating_arrow.svg?react';
import Header from 'src/components/header/Header';
import Spinner from 'src/components/spinner/Spinner';
import Search from 'src/components/search/unit-search/UnitSearch';
import Footer from 'src/components/footer/Footer';
import Button from 'src/components/button/Button';
import RadioGroup from "src/components/radio-group/RadioGroup";
import { useHistory } from 'react-router-dom';
import { useSllGetLessonsForSaLazyQuery, SllLesson } from '../../../api/graphql-codegen';
import Select from '@src/components/select/Select';
import LessonProgressModal from '@src/components/lesson-progress-modal/LessonProgressModal';
import { superAdminStandaloneLessonsFilterOptions } from '@constants';
import Pagination from '@src/components/pagination/Pagination';
import { debounce } from '@src/utils/helpers';
import ResponsiveCard from './responsive-card/ResponsiveCard';
import FooterPage from '@src/components/footer-page/FooterPage';

type TableStates = {
  keyword: string
  sortBy: 'name' | 'country'
  order: 'ASC' | 'DESC'
  page: number
  perPage: number
  ageGroup?: string
}

export const sortOptions: Array<{label: string, value: 'name' | 'country'}> = [
  {label: "Lesson Name", value: "name"},
  {label: "Country", value: "country"}
]

export const orderOptions: Array<{label: string, value: 'ASC' | 'DESC'}> = [
  {label: "Ascending", value: "ASC"},
  {label: "Descending", value: "DESC"}
]

const defaultStates = {
  keyword: '',
  sortBy: 'name' as 'name' | 'country',
  order: 'ASC' as 'ASC' | 'DESC',
  page: 1,
  perPage: 24
}

function SuperLessons() {
  const [states, setStates] = useState<TableStates>(defaultStates)
  const [showLesson, setShowLesson] = useState(false);
  const [selLesson, setSelLesson] = useState<SllLesson>();
  const history = useHistory();

  const [sllGetLessons, {data, loading}] = useSllGetLessonsForSaLazyQuery({
    variables: { input: { keyword: '' }}, fetchPolicy: 'network-only'});

  const debouncedApiCall = useRef(
    debounce((states: TableStates) => sllGetLessons({variables: {input: states}}), 500)
  ).current;

  // First Load
  useEffect(() => {
    debouncedApiCall(defaultStates)
  }, [debouncedApiCall])

  const tableData = useMemo(() => {
    if (data?.sllGetLessonsForSA) {
      return {
        list: data?.sllGetLessonsForSA?.data ?? [],
        totals: data?.sllGetLessonsForSA.totals
      }
    }
    return {
      list: [],
      totals: 0
    }
  }, [data]);

  const handleOpenNew = () => {
    history.push('/super/lessons/new');
  };

  const handleEditLesson = (lesson: SllLesson) => {
    history.push(`/super/lessons/edit/${lesson.id}`);
  };

  const handleOpenLesson = (lesson: SllLesson) => {
    if (lesson) {
      setShowLesson(true);
      setSelLesson(lesson);
    }
  };

  const handleStatesChange = useCallback((key: string, value: any) => {
    setStates((prev) => {
      const newStates = {
        ...prev,
        [key]: value,
        ...((key === 'keyword' || key === 'ageGroup') && { page: 1 })
      }
      debouncedApiCall(newStates)

      return newStates
    })
  }, [debouncedApiCall])

  const onResetClick = useCallback(() => {
    setStates(defaultStates)
    debouncedApiCall(defaultStates)
  }, [debouncedApiCall])

  const handleCloseModal = () => {setShowLesson(false)}

  return (
    <>
      <Header />
      <div className={styles.page}>
        <div className={styles.actions}>
          <div className={styles.title}>Edit Standalone Lessons</div>
          <Button onClick={handleOpenNew} size={'small'} label={'Create Lesson'}/>
        </div>
        <div className={styles.header}>
          <div className={styles.filters}>
            <Search
              onChange={v => handleStatesChange('keyword', v)}
              value={states.keyword}
              placeholder={"Search Lesson..."}
            />
            <Select
              inDropDown={true}
              label={'Age Group'}
              options={superAdminStandaloneLessonsFilterOptions}
              value={states.ageGroup}
              onChange={v => handleStatesChange('ageGroup', v.value)}
              doNotSort={true}
            />
            <Button
              onClick={onResetClick}
              className={styles.resetBtn}
              label={'Reset'}
              size={'extraSmall'} outline={true} color={'green'}
              leftIcon={<IconRotating className={styles.resetIcon}/>}
            />
          </div>
          <div className={styles.sort}>
            <div>
              <div className={styles.sortTitle}>Sort by:</div>
              <RadioGroup
                value={states.sortBy}
                options={sortOptions}
                onChange={v => handleStatesChange('sortBy', v)}
                name={''}
                hasError={false}
                subTitle={""}
              />
            </div>
            <div>
              <div className={styles.sortTitle}>Order:</div>
              <RadioGroup
                value={states.order}
                options={orderOptions}
                onChange={v => handleStatesChange('order', v)}
                name={''}
                hasError={false}
                subTitle={""}
              />
            </div>
          </div>
          <div className={styles.count}>
            <div className={styles.unitsLength}>{tableData?.totals} <div className={styles.unitsText}>Lessons</div></div>
          </div>
        </div>
        <div className={styles.lessonTitle}>Lessons</div>
        {loading
          ? <div className={styles.spinner}><Spinner/></div>
          : <>
              <div className={styles.unitsDiv}>
                {tableData.list.map((item, index) => (
                  <ResponsiveCard
                    key={index}
                    lesson={item}
                    handleEditLesson={handleEditLesson}
                    handleOpenLesson={handleOpenLesson}
                  />
                ))}
              </div>
              <div style={{marginTop: '1rem'}}>
                <Pagination
                  perPage={states.perPage}
                  currentPage={states.page}
                  totalItems={tableData.totals}
                  onPageChange={v => handleStatesChange('page', v)}
                />
              </div>
            </>
        }
      </div>
      <FooterPage />
      {/* <Footer/> */}
      {(showLesson && selLesson?.id) &&
        <LessonProgressModal open={showLesson} onClose={handleCloseModal} lessonId={selLesson.id} lessonlist={tableData.list}/>
      }
    </>
  );
}

export default SuperLessons;
